import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    primary: {
      main: '#6c4cff',        // Electric violet
      light: '#8a73ff',
      dark: '#4a2bff',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#d4ff3e',        // Lime accent
      light: '#e8ff99',
      dark: '#b8e83a',
      contrastText: '#14201a',
    },
    success: {
      main: '#2ee0c2',        // Teal
      light: '#4ee5d0',
      dark: '#1fa898',
    },
    error: {
      main: '#ff6b8a',        // Coral
      light: '#ff9aad',
      dark: '#cc3b57',
    },
    warning: {
      main: '#ffb37a',        // Peach
      light: '#ffcc99',
      dark: '#cc7a3f',
    },
    info: {
      main: '#4cc5ff',        // Sky
      light: '#8dd9ff',
      dark: '#1a8ec9',
    },
    background: {
      default: '#f7f6fb',     // Surface 2
      paper: '#ffffff',       // Surface
    },
    text: {
      primary: '#14132a',     // Ink
      secondary: '#3d3a5a',   // Ink 2
      disabled: '#9b97b6',    // Ink 4
    },
    divider: '#e7e4f3',       // Line
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif',
    h1: {
      fontFamily: '"Bricolage Grotesque", "Inter", sans-serif',
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.03em',
    },
    h2: {
      fontFamily: '"Bricolage Grotesque", "Inter", sans-serif',
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontFamily: '"Bricolage Grotesque", "Inter", sans-serif',
      fontSize: '1.5rem',
      fontWeight: 700,
      letterSpacing: '-0.015em',
    },
    h4: {
      fontFamily: '"Bricolage Grotesque", "Inter", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontFamily: '"Bricolage Grotesque", "Inter", sans-serif',
      fontSize: '1.125rem',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Bricolage Grotesque", "Inter", sans-serif',
      fontSize: '1rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.5px',
      fontSize: '0.875rem',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '11px',
          padding: '10px 16px',
          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        containedPrimary: {
          background: '#14132a',
          color: '#ffffff',
          '&:hover': {
            background: '#2a2746',
            boxShadow: '0 4px 14px -4px rgba(108, 76, 255, 0.4)',
          },
        },
        containedSecondary: {
          background: '#d4ff3e',
          color: '#14201a',
          boxShadow: '0 4px 14px -4px rgba(212, 255, 62, 0.8)',
          '&:hover': {
            background: '#b8e83a',
            boxShadow: '0 8px 20px -4px rgba(212, 255, 62, 0.9)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: '#d8d3ea',
          '&:hover': {
            borderWidth: '1px',
            backgroundColor: '#efedf8',
          },
        },
        text: {
          color: '#3d3a5a',
          '&:hover': {
            backgroundColor: '#efedf8',
            color: '#14132a',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '22px',
          border: '1px solid #e7e4f3',
          boxShadow: '0 1px 2px rgba(20, 19, 42, 0.04), 0 1px 3px rgba(20, 19, 42, 0.03)',
          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 14px -4px rgba(20, 19, 42, 0.08), 0 2px 6px -2px rgba(20, 19, 42, 0.04)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 2px rgba(20, 19, 42, 0.04), 0 1px 3px rgba(20, 19, 42, 0.03)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(247, 246, 251, 0.7)',
          backdropFilter: 'blur(18px)',
          color: '#14132a',
          boxShadow: 'none',
          borderBottom: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          border: '1px solid #e7e4f3',
          borderRadius: '0',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '11px',
          margin: '4px 8px',
          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: '#efedf8',
            transform: 'translateX(4px)',
          },
          '&.Mui-selected': {
            backgroundColor: '#14132a',
            color: '#ffffff',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#1f1d3a',
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '11px',
            backgroundColor: '#ffffff',
            transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: '#f7f6fb',
            },
            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              '& fieldset': {
                borderColor: '#6c4cff',
                borderWidth: '2px',
                boxShadow: '0 0 0 4px rgba(108, 76, 255, 0.12)',
              },
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d8d3ea',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: '0',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e7e4f3',
          padding: '12px 16px',
          fontSize: '0.875rem',
        },
        head: {
          backgroundColor: '#f7f6fb',
          fontWeight: 600,
          borderBottom: '2px solid #e7e4f3',
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.08em',
          color: '#9b97b6',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: '#f7f6fb',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
          fontWeight: 600,
          fontSize: '0.75rem',
        },
        filled: {
          backgroundColor: '#ede7ff',
          color: '#4a2bff',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: 'none',
          fontSize: '0.875rem',
        },
        standardInfo: {
          backgroundColor: '#ede7ff',
          color: '#4a2bff',
        },
        standardSuccess: {
          backgroundColor: '#ecfbb6',
          color: '#2c4a00',
        },
        standardWarning: {
          backgroundColor: '#fff1d9',
          color: '#8a5a00',
        },
        standardError: {
          backgroundColor: '#ffe4ec',
          color: '#b81b50',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          height: '6px',
          backgroundColor: '#e7e4f3',
        },
        bar: {
          background: 'linear-gradient(90deg, #6c4cff, #ff6b8a)',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: '38px',
          height: '22px',
          padding: '0',
        },
        switchBase: {
          padding: '3px',
          '&.Mui-checked': {
            color: '#ffffff',
            '& + .MuiSwitch-track': {
              backgroundColor: '#6c4cff',
            },
          },
        },
        thumb: {
          width: '16px',
          height: '16px',
        },
        track: {
          backgroundColor: '#d8d3ea',
          opacity: '1',
          borderRadius: '999px',
        },
      },
    },
  },
  shape: {
    borderRadius: 14,
  },
});
